import { EventType, useI18N } from "@sundae/react-hooks";
import { EContractVersion, IComposedTx } from "@sundaeswap/core";
import { useMutation } from "@tanstack/react-query";
import { Tx, TxComplete } from "lucid-cardano";
import { useCallback } from "react";
import { useMigrateLiquidityContext } from "../../components/Dialogs/MigrateLiquidityDialog/context";
import { EMigrationTransactionBuilderState } from "../../components/Dialogs/MigrateLiquidityDialog/types";
import { useAppContext } from "../../stores";
import { ETransactionState } from "../../types/Transaction.types";
import { TransactionErrorHandler } from "../../utils/transactions/TransactionErrorHandler.class";
import { useEventTracking } from "../useEventTracking";
import { ITransactionErrorShape, useTransactionErrorHandler } from "../useTransactionErrorHandler";
import { useMigrateLiquidityConfig } from "./useMigrateLiquidityConfig";

export const useMigrateLiquidityTx = () => {
  const { t } = useI18N("transactions");
  const {
    derived: { selectedPools },
    handlers: { setTransactionBuilderState, setCompleteTx, setDialogState, setBuiltTx },
    transaction: { builtTx },
  } = useMigrateLiquidityContext();
  const { handleTransactionError } = useTransactionErrorHandler(() =>
    setDialogState(ETransactionState.error)
  );
  const migrationConfig = useMigrateLiquidityConfig();
  const handleEventTracking = useEventTracking();

  const {
    state: { sdk },
  } = useAppContext();

  const handleCancelTx = useCallback(() => {
    setDialogState(ETransactionState.submittingCancelled);
    setTransactionBuilderState?.(undefined);
  }, [setDialogState, setTransactionBuilderState]);

  const buildTxMutation = useMutation(
    [migrationConfig, sdk],
    async () => {
      if (!sdk || !migrationConfig) {
        return;
      }

      return TransactionErrorHandler.buildTransaction(() => {
        return sdk.builder(EContractVersion.V1).migrateLiquidityToV3(migrationConfig);
      });
    },
    {
      cacheTime: 0,
      onError({ error, transactionState }: ITransactionErrorShape) {
        if (!migrationConfig) return;

        setTransactionBuilderState(EMigrationTransactionBuilderState.ERROR);
        handleTransactionError(
          error,
          {
            resolutionAction: handleCancelTx,
            resolutionButtonText: t("transaction.error_reset"),
          },
          transactionState,
          migrationConfig
        );
      },
      onMutate() {
        setTransactionBuilderState?.(EMigrationTransactionBuilderState.BUILD);
      },
      async onSuccess(tx: IComposedTx<Tx, TxComplete>) {
        if (!tx) return;
        const completeTx = await tx.build();
        setTransactionBuilderState?.(EMigrationTransactionBuilderState.SUCCESS);
        setBuiltTx?.(tx);
        setCompleteTx?.(completeTx);
      },
    }
  );

  const migrationMutation = useMutation(
    [builtTx],
    async () =>
      TransactionErrorHandler.handleSDKMutation(
        // we are not passing a buildFn here because we already have a built tx and the swap is only executed on the exchange preview screen.
        // this screen will only be available for users that have successfully built a tx.
        undefined,
        builtTx
      ),
    {
      cacheTime: 0,
      onError({ error, transactionState, cbor }: ITransactionErrorShape) {
        if (!migrationConfig) return;
        handleTransactionError(
          error,
          {
            resolutionAction: handleCancelTx,
            resolutionButtonText: t("transaction.error_reset"),
          },
          transactionState,
          migrationConfig,
          cbor
        );
        setDialogState(ETransactionState.error);
      },
      onMutate() {
        setDialogState(ETransactionState.submitting);
        handleEventTracking({
          eventType: EventType.Event,
          source: "Swap",
          value: `migration_execution_for_[${selectedPools
            .map(({ V1: v1Pool }) => v1Pool.ident)
            .join(",")}]`,
        });
      },
      onSuccess(hash) {
        if (!hash) return;
        setDialogState(ETransactionState.success);
        return hash;
      },
    }
  );

  return {
    handleTxBuilding: buildTxMutation,
    handleMigration: migrationMutation,
  };
};
